/**
 * Rugby Culture - Cart Drawer
 * Pairs with sections/rc-cart-drawer.liquid and assets/rc-cart-drawer.css
 *
 * Integration required:
 * 1. Add the attribute data-cart-drawer-toggle to the existing header
 *    cart icon (link or button) so it opens this drawer.
 * 2. Load this file with `defer` in theme.liquid, after rc-cart-drawer.css.
 * 3. The header cart-count badge (data-cart-count) is updated automatically
 *    after every add/change/remove.
 */
(function () {
  const SECTION_ID = 'rc-cart-drawer';

  const drawer = document.getElementById('rc-cart-drawer');
  const overlay = document.querySelector('[data-cart-drawer-overlay]');
  if (!drawer || !overlay) return;

  function getContentEl() {
    return document.getElementById('rc-cart-drawer-content');
  }

  function setLoading(isLoading) {
    const content = getContentEl();
    if (content) content.classList.toggle('is-loading', isLoading);
  }

  function openDrawer() {
    drawer.classList.add('is-open');
    overlay.classList.add('is-visible');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rc-drawer-open');
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('rc-drawer-open');
  }

  function isOpen() {
    return drawer.classList.contains('is-open');
  }

  // Refetch this section's markup so all schema-driven content (copy,
  // thresholds, cross-sell blocks) stays in sync with the live cart.
  async function refreshDrawer() {
    setLoading(true);
    try {
      const url = window.location.pathname + window.location.search +
        (window.location.search ? '&' : '?') + 'section_id=' + SECTION_ID;
      const response = await fetch(url);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const freshContent = parsed.getElementById('rc-cart-drawer-content');
      const currentContent = getContentEl();
      if (freshContent && currentContent) {
        currentContent.innerHTML = freshContent.innerHTML;
      }
    } catch (err) {
      console.error('Rugby Culture cart drawer: failed to refresh.', err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCartJson() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  async function notifyCartUpdated() {
    const cart = await fetchCartJson();
    document.dispatchEvent(new CustomEvent('rc:cart:updated', { detail: { cart } }));
    document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
      badge.textContent = cart.item_count;
      badge.hidden = cart.item_count === 0;
    });
  }

  async function changeLineQuantity(lineKey, quantity) {
    setLoading(true);
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('Rugby Culture cart drawer:', error.description || error.message);
      }
      await refreshDrawer();
      await notifyCartUpdated();
    } catch (err) {
      console.error('Rugby Culture cart drawer: quantity change failed.', err);
      setLoading(false);
    }
  }

  async function addVariantToCart(variantId) {
    setLoading(true);
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!response.ok) {
        const error = await response.json();
        alert(error.description || 'This item could not be added to your bag.');
        setLoading(false);
        return;
      }
      await refreshDrawer();
      await notifyCartUpdated();
      openDrawer();
    } catch (err) {
      console.error('Rugby Culture cart drawer: add to cart failed.', err);
      setLoading(false);
    }
  }

  // ---------- Delegated events (survive the innerHTML refresh) ----------
  drawer.addEventListener('click', function (event) {
    const closeBtn = event.target.closest('[data-cart-drawer-close]');
    if (closeBtn) {
      closeDrawer();
      return;
    }

    const decreaseBtn = event.target.closest('[data-cart-qty-decrease]');
    const increaseBtn = event.target.closest('[data-cart-qty-increase]');
    const removeBtn = event.target.closest('[data-cart-remove]');
    const addBtn = event.target.closest('[data-cart-add]');

    if (decreaseBtn || increaseBtn) {
      const btn = decreaseBtn || increaseBtn;
      const lineKey = btn.getAttribute('data-line-key');
      const row = btn.closest('.rc-item');
      const qtyEl = row ? row.querySelector('[data-cart-qty-value]') : null;
      const currentQty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
      const newQty = decreaseBtn ? Math.max(0, currentQty - 1) : currentQty + 1;
      changeLineQuantity(lineKey, newQty);
      return;
    }

    if (removeBtn) {
      changeLineQuantity(removeBtn.getAttribute('data-line-key'), 0);
      return;
    }

    if (addBtn && !addBtn.disabled) {
      addVariantToCart(addBtn.getAttribute('data-variant-id'));
      return;
    }
  });

  overlay.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen()) closeDrawer();
  });

  // Toggle button lives outside this section (in the header).
  document.addEventListener('click', function (event) {
    const toggle = event.target.closest('[data-cart-drawer-toggle]');
    if (!toggle) return;
    event.preventDefault();
    isOpen() ? closeDrawer() : openDrawer();
  });

  // Intercept every add-to-cart form sitewide (PDP, quick add, upsell blocks)
  // so the drawer opens instead of a full page navigation.
  document.addEventListener('submit', async function (event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || form.action.indexOf('/cart/add') === -1) return;

    event.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      if (!response.ok) {
        const error = await response.json();
        alert(error.description || 'This item could not be added to your bag.');
        setLoading(false);
        return;
      }
      await refreshDrawer();
      await notifyCartUpdated();
      openDrawer();
    } catch (err) {
      console.error('Rugby Culture cart drawer: add to cart failed.', err);
      setLoading(false);
    }
  });
})();